import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  CreditCard, Plus, Edit, Trash2, Search, ArrowLeft 
} from 'lucide-react';
import { visaTypesApi, countriesApi } from '../lib/api';
import CountryFlag from '../components/CountryFlag';
import { toast } from 'sonner';

const AdminVisaTypes = ({ isNew = false }) => {
  const navigate = useNavigate();
  const [visaTypes, setVisaTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(isNew);
  const [editingVisa, setEditingVisa] = useState(null);
  const [formData, setFormData] = useState({
    country_id: '',
    name: '',
    visa_type: 'tourist',
    processing_time: '',
    validity: '',
    max_stay: '',
    fee: 0,
    currency: 'INR',
    requirements: [],
    documents_required: [],
    is_popular: false,
    appointment_enabled: false,
    appointment_fee: 0,
    appointment_duration: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [visasRes, countriesRes] = await Promise.all([
        visaTypesApi.getAll(),
        countriesApi.getAll()
      ]);
      setVisaTypes(visasRes.data);
      setCountries(countriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, fee: parseFloat(formData.fee), appointment_fee: parseFloat(formData.appointment_fee) || 0 };
      if (editingVisa) {
        await visaTypesApi.update(editingVisa.visa_type_id, data);
        toast.success('Visa type updated successfully');
      } else {
        await visaTypesApi.create(data);
        toast.success('Visa type created successfully');
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving visa type:', error);
      toast.error('Failed to save visa type');
    }
  };

  const handleDelete = async (visaTypeId) => {
    if (!window.confirm('Are you sure you want to delete this visa type?')) {
      return;
    }
    try {
      await visaTypesApi.delete(visaTypeId);
      toast.success('Visa type deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting visa type:', error);
      toast.error('Failed to delete visa type');
    }
  };

  const handleEdit = (visa) => {
    setEditingVisa(visa);
    setFormData({
      country_id: visa.country_id,
      name: visa.name,
      visa_type: visa.visa_type,
      processing_time: visa.processing_time,
      validity: visa.validity,
      max_stay: visa.max_stay,
      fee: visa.fee,
      currency: visa.currency,
      requirements: visa.requirements || [],
      documents_required: visa.documents_required || [],
      is_popular: visa.is_popular,
      appointment_enabled: visa.appointment_enabled || false,
      appointment_fee: visa.appointment_fee || 0,
      appointment_duration: visa.appointment_duration || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingVisa(null);
    setFormData({
      country_id: '',
      name: '',
      visa_type: 'tourist',
      processing_time: '',
      validity: '',
      max_stay: '',
      fee: 0,
      currency: 'INR',
      requirements: [],
      documents_required: [],
      is_popular: false,
      appointment_enabled: false,
      appointment_fee: 0,
      appointment_duration: ''
    });
    if (isNew) navigate('/admin/visa-types');
  };

  const filteredVisaTypes = visaTypes.filter(visa =>
    visa.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCountryName = (countryId) => {
    const country = countries.find(c => c.country_id === countryId);
    return country ? `${country.flag_emoji} ${country.name}` : 'Unknown';
  };

  if (showForm) {
    return (
      <div className="min-h-screen bg-[#F5F0E6]">
        <DashboardSidebar />
        <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8">
          <Button variant="ghost" onClick={resetForm} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Visa Types
          </Button>

          <div className="bg-white rounded-xl p-6 border border-[#D1CBBF] max-w-5xl">
            <h1 className="text-2xl font-bold text-[#0A1E40] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {editingVisa ? 'Edit Visa Type' : 'Add New Visa Type'}
            </h1>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Country</Label>
                <Select value={formData.country_id} onValueChange={(value) => setFormData({ ...formData, country_id: value })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a country"/>
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.country_id} value={country.country_id}>
                        <span className="inline-flex items-center gap-2"><CountryFlag code={country.code} size="sm" /> {country.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1 mt-0 sm:mt-0">
                  <Label>Visa Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Tourist Visa"
                    required
                    className="mt-1.5"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1 mt-0 sm:mt-0">
                  <Label>Visa Type</Label>
                  <Select value={formData.visa_type} onValueChange={(value) => setFormData({ ...formData, visa_type: value })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tourist">Tourist</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                      <SelectItem value="work">Work</SelectItem>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="transit">Transit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-3 sm:col-span-1">
                  <Label>Processing Time</Label>
                  <Input
                    value={formData.processing_time}
                    onChange={(e) => setFormData({ ...formData, processing_time: e.target.value })}
                    placeholder="e.g., 3-5 days"
                    required
                    className="mt-1.5"
                  />
                </div>
                <div className="col-span-3 sm:col-span-1">
                  <Label>Validity</Label>
                  <Input
                    value={formData.validity}
                    onChange={(e) => setFormData({ ...formData, validity: e.target.value })}
                    placeholder="e.g., 6 months"
                    required
                    className="mt-1.5"
                  />
                </div>
                <div className="col-span-3 sm:col-span-1">
                  <Label>Max Stay</Label>
                  <Input
                    value={formData.max_stay}
                    onChange={(e) => setFormData({ ...formData, max_stay: e.target.value })}
                    placeholder="e.g., 30 days"
                    required
                    className="mt-1.5"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fee (in {formData.currency})</Label>
                  <Input
                    type="number"
                    value={formData.fee}
                    onChange={(e) => setFormData({ ...formData, fee: e.target.value })}
                    placeholder="e.g., 5000"
                    required
                    className="mt-1.5"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={formData.is_popular}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_popular: checked })}
                  />
                  <Label>Popular Visa</Label>
                </div>
              </div>

              {/* Appointment Fees Section */}
              <div className="col-span-2 border-t border-[#E8E2D6] pt-4 mt-2">
                <div className="flex items-center gap-3 mb-4 bg-[#F5F0E6] rounded-xl p-4">
                  <Switch
                    id="appointment-toggle"
                    checked={!!formData.appointment_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, appointment_enabled: checked })}
                    data-testid="appointment-toggle"
                  />
                  <div onClick={() => setFormData({ ...formData, appointment_enabled: !formData.appointment_enabled })} className="cursor-pointer flex-1">
                    <Label className="font-semibold cursor-pointer text-[#0A1E40]">Appointment Booking {formData.appointment_enabled ? '(Enabled)' : '(Disabled)'}</Label>
                    <p className="text-xs text-[#4A5568]">Enable if this visa requires an appointment (biometric/interview)</p>
                  </div>
                </div>
                {formData.appointment_enabled && (
                  <div className="grid grid-cols-2 gap-4 bg-[#F5F0E6] rounded-xl p-4">
                    <div>
                      <Label>Appointment Fee (₹)</Label>
                      <Input
                        type="number"
                        value={formData.appointment_fee}
                        onChange={(e) => setFormData({ ...formData, appointment_fee: parseFloat(e.target.value) || 0 })}
                        placeholder="e.g., 5000"
                        data-testid="appointment-fee-input"
                      />
                    </div>
                    <div>
                      <Label>Approximate Duration</Label>
                      <Input
                        value={formData.appointment_duration}
                        onChange={(e) => setFormData({ ...formData, appointment_duration: e.target.value })}
                        placeholder="e.g., 15-30 mins"
                        data-testid="appointment-duration-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label>Requirements (one per line)</Label>
                <Textarea
                  value={formData.requirements.join('\n')}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value.split('\n').filter(r => r.trim()) })}
                  placeholder="Valid passport&#10;Financial documents&#10;..."
                  className="mt-1.5"
                  rows={3}
                />
              </div>

              <div>
                <Label>Documents Required (one per line)</Label>
                <Textarea
                  value={formData.documents_required.join('\n')}
                  onChange={(e) => setFormData({ ...formData, documents_required: e.target.value.split('\n').filter(r => r.trim()) })}
                  placeholder="Passport&#10;Photos&#10;Bank statements&#10;..."
                  className="mt-1.5"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-[#FF5A36] hover:bg-[#E0492B]">
                  {editingVisa ? 'Update Visa Type' : 'Create Visa Type'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar />
      
      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="admin-visa-types-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Manage Visa Types
            </h1>
            <p className="text-[#4A5568] mt-1">{visaTypes.length} visa types configured</p>
          </div>
          <Button 
            onClick={() => setShowForm(true)} 
            className="bg-[#FF5A36] hover:bg-[#E0492B]"
            data-testid="add-visa-type-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Visa Type
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF] pointer-events-none" />
            <Input
              type="text"
              placeholder="Search visa types by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12"
            />
          </div>
        </div>

        {/* Visa Types Table */}
        <div className="bg-white rounded-xl border border-[#D1CBBF] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Visa Name</th>
                  <th>Type</th>
                  <th>Processing</th>
                  <th>Validity</th>
                  <th>Fee</th>
                  <th>Popular</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8">
                      <div className="animate-pulse">Loading...</div>
                    </td>
                  </tr>
                ) : filteredVisaTypes.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-[#4A5568]">
                      No visa types found
                    </td>
                  </tr>
                ) : (
                  filteredVisaTypes.map((visa) => (
                    <tr key={visa.visa_type_id} data-testid={`visa-row-${visa.visa_type_id}`}>
                      <td>{getCountryName(visa.country_id)}</td>
                      <td className="font-medium text-[#0A1E40]">{visa.name}</td>
                      <td className="capitalize">{visa.visa_type}</td>
                      <td>{visa.processing_time}</td>
                      <td>{visa.validity}</td>
                      <td>₹{visa.fee?.toLocaleString()}</td>
                      <td>
                        {visa.is_popular && (
                          <span className="bg-[#FF5A36] text-white text-xs px-2 py-1 rounded-full">Popular</span>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(visa)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(visa.visa_type_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminVisaTypes;
